import React from 'react';
import { 
  MessageCircle, 
  Brain, 
  Lightbulb, 
  ShieldCheck, 
  Target, 
  Zap, 
  Lock,
  Briefcase,
  TrendingUp,
  Home as HomeIcon,
  Sparkles,
  Star,
  Apple,
  Download
} from 'lucide-react';
import { 
  heroData, 
  statsData,
  howItWorksSteps, 
  keyBenefits, 
  useCases, 
  whyFindClarity,
  testimonials,
  faqs 
} from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const iconMap = {
  'message-circle': MessageCircle,
  'brain': Brain,
  'lightbulb': Lightbulb,
  'shield-check': ShieldCheck,
  'target': Target,
  'zap': Zap,
  'lock': Lock,
  'briefcase': Briefcase,
  'trending-up': TrendingUp,
  'home': HomeIcon,
  'sparkles': Sparkles
};

const Home = () => {
  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent size={32} /> : null;
  };

  return (
    <div className="home-container">
      {/* Hero Section - Full Width with Overlay */}
      <section className="hero-section-modern" id="hero">
        <div className="hero-background-overlay"></div>
        <img 
          src={heroData.heroImage} 
          alt="Find Clarity App"
          className="hero-background-image"
        />
        <div className="hero-content-overlay">
          <div className="hero-text-modern">
            <h1 className="hero-title-modern">{heroData.headline}</h1>
            <p className="hero-subtitle-modern">{heroData.subheading}</p>
            <div className="hero-cta-buttons-modern">
              <a
                href={heroData.ctaButtons[0].link}
                className="btn-store-badge-modern"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Apple size={28} />
                <div className="store-badge-text">
                  <span className="store-badge-label">Download on the</span>
                  <span className="store-badge-name">App Store</span>
                </div>
              </a>
              <a
                href={heroData.ctaButtons[1].link}
                className="btn-store-badge-modern"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="store-badge-text">
                  <span className="store-badge-label">GET IT ON</span>
                  <span className="store-badge-name">Google Play</span>
                </div>
              </a>
            </div>
            <p className="hero-free-badge-modern">100% Free Forever • No Ads • No Subscriptions</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {statsData.map((stat) => (
              <div key={stat.id} className="stat-card">
                <h3 className="stat-number">{stat.number}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section - Visual Layout */}
      <section className="section-container section-white" id="how-it-works">
        <div className="container">
          <div className="section-header-centered">
            <h2 className="section-title-large">How Find Clarity Works</h2>
            <p className="section-subtitle-large">Three simple steps to better thinking and confident decisions</p>
          </div>
          
          <div className="steps-grid-modern">
            {howItWorksSteps.map((step) => (
              <div key={step.id} className="step-card-modern">
                <div className="step-number-modern">{step.id}</div>
                <div className="step-icon-modern">{getIcon(step.icon)}</div>
                <h3 className="step-title-modern">{step.title}</h3>
                <p className="step-description-modern">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits Section - Image + Text Layout */}
      <section className="section-container section-visual-split" id="benefits">
        <div className="container-wide">
          <div className="visual-split-layout">
            <div className="visual-split-image">
              <img 
                src="https://images.unsplash.com/photo-1618425977996-bebc5afe88f9?w=800&q=80" 
                alt="Person experiencing clarity and peace"
                className="split-image"
              />
            </div>
            <div className="visual-split-content">
              <h2 className="section-title-large">Why People Love Find Clarity</h2>
              <p className="section-subtitle-large">Transform how you think, decide, and take action</p>
              
              <div className="benefits-list">
                {keyBenefits.map((benefit) => (
                  <div key={benefit.id} className="benefit-item">
                    <div className="benefit-icon-modern">{getIcon(benefit.icon)}</div>
                    <div className="benefit-content">
                      <h3 className="benefit-title-modern">{benefit.title}</h3>
                      <p className="benefit-description-modern">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - Large Visual Cards */}
      <section className="section-container section-light">
        <div className="container">
          <div className="section-header-centered">
            <h2 className="section-title-large">Built for Real Life</h2>
            <p className="section-subtitle-large">Whatever you're facing, Find Clarity helps you think it through</p>
          </div>
          
          <div className="use-cases-grid-large">
            {useCases.map((useCase) => (
              <div key={useCase.id} className="use-case-card-large">
                <div className="use-case-image-wrapper-large">
                  <img 
                    src={useCase.image} 
                    alt={useCase.title}
                    className="use-case-image-large"
                  />
                  <div className="use-case-overlay"></div>
                </div>
                <div className="use-case-content-large">
                  <div className="use-case-icon-large">{getIcon(useCase.icon)}</div>
                  <h3 className="use-case-title-large">{useCase.title}</h3>
                  <p className="use-case-description-large">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Find Clarity - Side by Side */}
      <section className="section-container section-white">
        <div className="container-wide">
          <div className="visual-split-layout visual-split-reverse">
            <div className="visual-split-content">
              <h2 className="section-title-large">Why Find Clarity is Different</h2>
              <p className="section-description-large">We're not just another AI chatbot. We're your thinking partner, designed specifically to bring clarity to confusion.</p>
              
              <div className="why-clarity-list">
                {whyFindClarity.map((item) => (
                  <div key={item.id} className="why-item">
                    <div className="why-check-icon">
                      <ShieldCheck size={24} color="#4fa2ff" />
                    </div>
                    <div className="why-content">
                      <h3 className="why-title-modern">{item.title}</h3>
                      <p className="why-description-modern">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="visual-split-image">
              <img 
                src="https://images.pexels.com/photos/289998/pexels-photo-289998.jpeg?w=800&q=80" 
                alt="Peaceful moment of clarity"
                className="split-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Enhanced Cards */}
      <section className="section-container section-testimonials">
        <div className="container">
          <div className="section-header-centered">
            <h2 className="section-title-large">Trusted by Thousands</h2>
            <p className="section-subtitle-large">Real people, real clarity, real results</p>
          </div>
          
          <div className="testimonials-grid-modern">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card-modern">
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="#4fa2ff" color="#4fa2ff" />
                  ))}
                </div>
                <p className="testimonial-text-modern">"{testimonial.testimonial}"</p>
                <div className="testimonial-author-modern">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className="testimonial-avatar-modern"
                  />
                  <div className="testimonial-author-details">
                    <p className="author-name-modern">{testimonial.name}</p>
                    <p className="author-role-modern">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-container section-white" id="faq">
        <div className="container">
          <div className="section-header-centered">
            <h2 className="section-title-large">Frequently Asked Questions</h2>
            <p className="section-subtitle-large">Everything you need to know about Find Clarity</p>
          </div>
          
          <div className="faq-container-modern">
            <Accordion type="single" collapsible className="faq-accordion-modern">
              {faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="faq-item">
                  <AccordionTrigger className="faq-question-modern">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer-modern">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final Download CTA Section - Full Width */}
      <section className="section-cta-modern" id="download">
        <div className="cta-background-pattern"></div>
        <div className="container">
          <div className="cta-content-modern">
            <Download size={48} className="cta-icon" />
            <h2 className="cta-title-modern">Ready to Think Clearly?</h2>
            <p className="cta-subtitle-modern">Join thousands who've found clarity. Download now and start making better decisions today.</p>
            
            <div className="cta-buttons-modern">
              <a
                href={heroData.ctaButtons[0].link}
                className="btn-store-badge-modern btn-store-white"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Apple size={28} />
                <div className="store-badge-text">
                  <span className="store-badge-label">Download on the</span>
                  <span className="store-badge-name">App Store</span>
                </div>
              </a>
              <a
                href={heroData.ctaButtons[1].link}
                className="btn-store-badge-modern btn-store-white"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="store-badge-text">
                  <span className="store-badge-label">GET IT ON</span>
                  <span className="store-badge-name">Google Play</span>
                </div>
              </a>
            </div>
            <p className="cta-free-text-modern">Free Forever • No Ads • No Subscriptions</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
